/**
 * Карточка звонка `/calls/<id>` (`GET /api/calls/{id}`): поля строки списка,
 * итог звонка, запись и расшифровка целиком. Адрес пересылают — по нему
 * разбирают конкретный звонок. Спека `docs/specs/2026-09-26-calls-list-v3-design.md`;
 * «Кому дозванивались» и звонки номера — `docs/specs/2026-10-20-call-route-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";

import { CallRoute } from "@/components/CallRoute";
import { CallTags } from "@/components/CallTags";
import { normPhone } from "@/lib/callRoute";
import { type CallItem, loadCall, type ObjectKind } from "@/lib/calls";
import { changeText, ObjectLink, Outcome, Talk, TYPE } from "@/pages/CallsList";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11.5px] text-fg-faint">{label}</span>
      <span className="text-[13px]">{children}</span>
    </div>
  );
}

export function Card({ c }: { c: CallItem }) {
  const navigate = useNavigate();
  // Клик по лиду/сделке — их звонки панелью в списке за день звонка.
  const openPanel = (kind: ObjectKind, id: number) => navigate(`/calls?tab=list&from=${c.at.slice(0, 10)}&panel=${kind}:${id}`);
  return (
    <>
      <section className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-4 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="поля звонка">
        <Field label="Время">
          <span className="tabular-nums">
            {c.at.slice(8, 10)}.{c.at.slice(5, 7)}.{c.at.slice(0, 4)} {c.at.slice(11, 19)}
          </span>
        </Field>
        <Field label="Тип">{TYPE[c.type_id] ?? `тип ${c.type_id}`}</Field>
        <Field label="Номер">
          {c.phone && normPhone(c.phone) ? (
            <Link to={`/calls/phone/${normPhone(c.phone)}`} className="font-mono tabular-nums text-accent hover:underline" title="Все звонки этого номера">
              {c.phone}
            </Link>
          ) : (
            <span className="font-mono tabular-nums">{c.phone ?? "—"}</span>
          )}
        </Field>
        <Field label="Сотрудник">{c.user ?? "—"}</Field>
        <Field label="Исход">
          <Outcome c={c} />
        </Field>
        <Field label="Разговор (модель)">
          <Talk c={c} />
        </Field>
        <Field label="Лид">
          <ObjectLink kind="lead" id={c.lead_id} status={c.lead_status} onOpen={openPanel} />
        </Field>
        <Field label="Сделка">
          <ObjectLink kind="deal" id={c.deal_id} status={c.deal_status} onOpen={openPanel} />
        </Field>
        <Field label="Линия">
          {c.portal || "—"}
          {c.line_note && <span className="ml-1 text-fg-muted">({c.line_note})</span>}
        </Field>
        <Field label="Bitrix">
          {c.code || "—"} <span className="text-fg-muted">{c.reason}</span>
        </Field>
        <Field label="Привязка звонка">{c.entity ? `${c.entity.type.toLowerCase()} ${c.entity.id ?? ""}` : "—"}</Field>
      </section>

      {(c.type_id === 2 || c.type_id === 3) && <CallRoute id={c.id} />}

      <section className="flex flex-col gap-1.5 text-[12.5px]" data-annot="итог звонка">
        <h2 className="text-[13px] font-medium">Итог звонка</h2>
        {!c.outcome ? (
          <span className="text-fg-faint">сверять не с чем — нет сотрудника CRM или лида/сделки</span>
        ) : c.outcome.changes.length === 0 ? (
          <span className="text-fg-muted">без правок: в окне звонка сотрудник не менял сделку и лид</span>
        ) : (
          c.outcome.changes.map((ch) => (
            <span key={`${ch.object}-${ch.field}`}>
              <span className="tabular-nums text-fg-faint">{ch.at.slice(11, 16)}</span> {ch.object === "lead" ? "лид" : "сделка"} · {changeText(ch)}
            </span>
          ))
        )}
      </section>

      <section className="flex flex-col gap-1.5" data-annot="теги звонка">
        <h2 className="text-[13px] font-medium">Теги</h2>
        <CallTags id={c.id} tags={c.tags ?? []} onFilter={(tag) => navigate(`/calls?tab=list&from=${c.at.slice(0, 10)}&tag=${encodeURIComponent(tag)}`)} />
      </section>

      {c.record && (
        <section className="flex flex-col gap-1.5" data-annot="запись звонка">
          <h2 className="text-[13px] font-medium">Запись</h2>
          <audio controls preload="none" src={`/api/calls/${c.id}/record`} className="w-full max-w-[40rem]" />
        </section>
      )}

      <section className="flex flex-col gap-1.5" data-annot="текст звонка">
        <h2 className="text-[13px] font-medium">Текст разговора</h2>
        {c.transcript ? (
          <div className="max-w-[64rem] whitespace-pre-wrap rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-3 text-[13px] leading-[1.5]">{c.transcript}</div>
        ) : (
          <span className="text-[12.5px] text-fg-faint">расшифровки нет</span>
        )}
      </section>
    </>
  );
}

export function CallCardPage() {
  const { callId = "" } = useParams();
  const id = Number(callId);
  const data = useQuery({ queryKey: ["call", id], queryFn: () => loadCall(id), enabled: id > 0 });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка звонка">
        <Link to={data.data ? `/calls?tab=list&from=${data.data.at.slice(0, 10)}` : "/calls?tab=list"} className="text-[12.5px] text-accent hover:underline">
          ← Звонки
        </Link>
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Звонок {callId}</h1>
      </div>
      {!(id > 0) ? (
        <div className="text-[12.5px] text-err">Неверный номер звонка.</div>
      ) : data.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Загружаю звонок…</div>
      ) : data.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить звонок: {String(data.error.message)}</div>
      ) : (
        <Card c={data.data} />
      )}
    </main>
  );
}
